import React,{useContext,useState} from 'react'
import ReactMarkdown from 'react-markdown'
import Icon from '../Text/Icon'
import Text from '../Text/Text'
import UserContext from '../../contexts/UserContext'
import classNames from 'classnames'
import {ToolCallList} from './ToolCallDisplay'

// Custom Markdown components for themed rendering inside chat bubbles
const MD_COMPONENTS={
    p: ({children}) => <p className="mb-1 leading-relaxed last:mb-0">{children}</p>,
    strong: ({children}) => <strong className="font-bold text-white">{children}</strong>,
    em: ({children}) => <em className="italic text-white/90">{children}</em>,
    code: ({inline,children}) => inline
        ? <code className="px-1 py-0.5 rounded text-[11px] font-mono bg-black/30 text-emerald-300">{children}</code>
        :<pre className="mt-1 p-2 rounded text-[11px] font-mono bg-black/30 text-emerald-200 overflow-x-auto whitespace-pre-wrap"><code>{children}</code></pre>,
    ul: ({children}) => <ul className="pl-4 mt-1 space-y-0.5 list-disc text-white/90">{children}</ul>,
    ol: ({children}) => <ol className="pl-4 mt-1 space-y-0.5 list-decimal text-white/90">{children}</ol>,
    li: ({children}) => <li className="text-sm">{children}</li>,
    a: ({href,children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-300 underline transition-colors hover:text-emerald-200">{children}</a>,
    h1: ({children}) => <h1 className="mt-2 mb-1 text-base font-bold text-white">{children}</h1>,
    h2: ({children}) => <h2 className="mt-2 mb-1 text-sm font-bold text-white">{children}</h2>,
    h3: ({children}) => <h3 className="mt-1 mb-0.5 text-sm font-semibold text-white">{children}</h3>,
    blockquote: ({children}) => <blockquote className="pl-2 italic border-l-2 border-white/30 text-white/70">{children}</blockquote>,
}

const MD_COMPONENTS_USER={
    ...MD_COMPONENTS,
    strong: ({children}) => <strong className="font-bold">{children}</strong>,
    em: ({children}) => <em className="italic">{children}</em>,
    code: ({inline,children}) => inline
        ? <code className="px-1 py-0.5 rounded text-[11px] font-mono bg-white/30">{children}</code>
        :<pre className="mt-1 p-2 rounded text-[11px] font-mono bg-white/20 overflow-x-auto whitespace-pre-wrap"><code>{children}</code></pre>,
    a: ({href,children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline transition-opacity hover:opacity-80">{children}</a>,
    ul: ({children}) => <ul className="pl-4 mt-1 space-y-0.5 list-disc">{children}</ul>,
    ol: ({children}) => <ol className="pl-4 mt-1 space-y-0.5 list-decimal">{children}</ol>,
}

// Helper to format action summary
function formatActionSummary(summary) {
    if(!summary) return null
    const actions=[]
    if(summary.nodesAdded>0) actions.push(`+${summary.nodesAdded} nodes`)
    if(summary.nodesModified>0) actions.push(`~${summary.nodesModified} modified`)
    if(summary.nodesDeleted>0) actions.push(`-${summary.nodesDeleted} deleted`)
    if(summary.edgesAdded>0) actions.push(`+${summary.edgesAdded} edges`)
    if(summary.edgesDeleted>0) actions.push(`-${summary.edgesDeleted} edges`)
    if(summary.layoutChanged) actions.push('layout updated')
    if(summary.suggestionsGenerated>0) actions.push(`${summary.suggestionsGenerated} suggestions`)
    return actions.length>0? actions.join(' • '):null
}

export default function Bubble({chat,index,deleteChatBubble,label,isProcessing=false,currentToolCalls=[],...props}) {
    const [user]=useContext(UserContext)
    const [showToolCalls,setShowToolCalls]=useState(false)
    const actionSummary=formatActionSummary(chat.summary)

    // Determine if this is an AI message with tool calls
    const hasToolCalls=chat.toolCalls&&chat.toolCalls.length>0
    const isAIMessage=chat.role==='model'||chat.user!==user.displayName

    return (
        <div className={classNames('block w-full mb-2 h-fit',(label&&chat.user===user.displayName)&&'!mb-4')}>
            <div className={`flex flex-col max-w-[75%] w-fit flex-shrink-0 gap-1 px-3 py-2.5 rounded-xl ${isAIMessage? 'ml-2 bg-primary':'bg-secondary-600 ml-auto'}`} aria-label={isAIMessage? 'AI message':'Your message'}>

                {/* Action summary — primary display for AI messages with changes */}
                {isAIMessage&&actionSummary&&(
                    <div className='flex gap-2 items-center px-2 py-1.5 mb-1 rounded bg-white/10'>
                        <Icon icon='check-circle' className='text-sm text-emerald-300' />
                        <Text className='flex-1 text-sm font-semibold text-white/90'>{actionSummary}</Text>
                        {chat.iterations&&chat.iterations>1&&(
                            <Text className='text-[10px] text-white/40'>{chat.iterations} steps</Text>
                        )}
                    </div>
                )}

                {/* Message content */}
                <Text className='w-full text-sm font-medium leading-relaxed text-left text-white'>
                    <ReactMarkdown components={isAIMessage? MD_COMPONENTS:MD_COMPONENTS_USER}>{`${chat.content.replace(/(?:\r\n|\r|\n)/g,'  \n')}`}</ReactMarkdown>
                </Text>

                {/* Tool calls toggle — collapsed secondary detail */}
                {isAIMessage&&hasToolCalls&&(
                    <button
                        onClick={() => setShowToolCalls(!showToolCalls)}
                        className="flex gap-2 items-center px-2 py-1 mt-1 text-left rounded transition-colors bg-white/5 hover:bg-white/10"
                    >
                        <Icon icon="code" className="text-xs text-white/50" />
                        <Text className="flex-1 text-xs text-white/60">
                            {chat.toolCalls.length} tool call{chat.toolCalls.length>1? 's':''}
                        </Text>
                        <Icon
                            icon={showToolCalls? 'chevron-up':'chevron-down'}
                            className="text-xs text-white/40"
                        />
                    </button>
                )}

                {/* Collapsible tool calls display */}
                {showToolCalls&&hasToolCalls&&(
                    <div className="p-2 mt-1 rounded bg-black/20">
                        <ToolCallList toolCalls={chat.toolCalls} />
                    </div>
                )}

                {/* Iterations count — only shown when no action summary */}
                {!actionSummary&&chat.iterations&&chat.iterations>1&&(
                    <Text className='text-xs text-white/50'>{chat.iterations} steps</Text>
                )}
            </div>
            {label&&chat.user!==user.displayName&&(
                <Text className='ml-2 text-xs text-left text-zinc-700'>{chat.user||'AI Assistant'}</Text>
            )}
        </div>
    )
}

/**
 * StreamingBubble - Shows real-time tool calls while processing
 */
export function StreamingBubble({toolCalls=[],thinkingState=null,streamingMessage=null}) {
    const [showDetails,setShowDetails]=useState(true)

    return (
        <div className="block mb-2 w-full h-fit">
            <div className="flex flex-col max-w-[75%] w-fit flex-shrink-0 gap-1 px-3 py-2.5 rounded-xl ml-2 bg-primary" aria-live="polite" aria-label="AI is thinking">
                {/* Thinking indicator */}
                {thinkingState&&(
                    <div className="flex gap-2 items-center px-3 py-2 mb-2 rounded border bg-white/10 border-white/20">
                        <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                        </div>
                        <Text className="text-sm text-white">
                            {thinkingState.message||`Thinking... (step ${thinkingState.iteration||1})`}
                        </Text>
                    </div>
                )}

                {/* Tool calls toggle */}
                {toolCalls.length>0&&(
                    <>
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="flex gap-2 items-center px-2 py-1 text-left rounded transition-colors bg-white/5 hover:bg-white/10"
                        >
                            <Icon icon="code" className="text-xs text-white/50" />
                            <Text className="flex-1 text-xs text-white/60">
                                {toolCalls.length} tool call{toolCalls.length>1? 's':''}
                            </Text>
                            <Icon
                                icon={showDetails? 'chevron-up':'chevron-down'}
                                className="text-xs text-white/40"
                            />
                        </button>

                        {/* Tool calls list */}
                        {showDetails&&(
                            <div className="p-2 mt-2 rounded bg-black/20">
                                <ToolCallList
                                    toolCalls={toolCalls}
                                    isProcessing={!streamingMessage}
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Streaming message preview */}
                {streamingMessage&&(
                    <Text className='mt-2 w-full text-sm font-medium leading-relaxed text-left text-white'>
                        <ReactMarkdown components={MD_COMPONENTS}>{streamingMessage}</ReactMarkdown>
                    </Text>
                )}
            </div>
            <Text className='ml-2 text-xs text-left text-zinc-700'>AI Assistant</Text>
        </div>
    )
}
